import { i18n } from 'translate/i18n'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import { formattedTime } from 'core/helpers/formatter'
import CircularProgress from '@material-ui/core/CircularProgress'
import Container from '@material-ui/core/Container'
import { Hidden } from 'augmentation/mui'
import Typography from '@material-ui/core/Typography'
import { DropType } from 'core/logic/drop/drop.types'
import { PurchaseType } from 'core/logic/purchase/purchase.types'
import { differenceInSeconds, isFuture } from 'date-fns'
import PaymentExpired from 'assets/images/payment-expired.png'
import BackButton from 'components/BackButton'
import StripeContainer from 'containers/Stripe/Stripe.container'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import DropInfoMobile from '../Payment/DropInfoMobile'
import Pix from './Methods/Pix.logic'
import Crypto from './Methods/Crypto.view'
import useStyles from './PendingPayment.style'
import OfferPaymentBanner from 'openspace/components/OfferPaymentBanner'
import { GalleryType } from 'core/logic/gallery/gallery.types'
import Countdown from '@onepercentio/one-ui/components/Countdown'
import LoaderDotsIndicator from '@onepercentio/one-ui/components/LoaderDotsIndicator'
import { CountdownTextModel } from '@onepercentio/one-ui/components/Countdown'

const calculateTimeLeft = (date: Date) => {
  if (isFuture(date)) return differenceInSeconds(date, new Date())
  else return 0
}

const loading = (
  <Box data-testid='loader' display='flex' flexGrow={1} alignItems='center'>
    <CircularProgress />
  </Box>
)
export type PaymentViewProps = {
  drop?: DropType | null
  purchase?: PurchaseType | null
  uuid: string
  gallery: GalleryType
} & ClickActions<'onMyCollection'>

export function PaymentViewContent({
  drop,
  purchase,
  onMyCollection,
  uuid,
}: PaymentViewProps) {
  const classes = useStyles()
  const expired = (
    <Box
      display='flex'
      flexDirection='column'
      flexGrow={1}
      justifyContent='center'
      alignItems='center'
      className={classes.expiredContainer}
      p={6}>
      <Box mb={4}>
        <img
          width={116}
          height={147}
          src={PaymentExpired}
          title={i18n.t('authenticated.pendingPayment.expiredPayment')}
          alt={i18n.t('authenticated.pendingPayment.expiredPayment')}
        />
      </Box>
      <Typography
        variant='body1'
        component='p'
        align='center'
        className={classes.spacing}>
        <strong>
          {i18n.t('authenticated.pendingPayment.expiredPurchase')}
        </strong>
      </Typography>
      <Typography variant='body1' component='p' align='center'>
        {i18n.t('authenticated.pendingPayment.tryAgain')}
      </Typography>
    </Box>
  )
  const [content, setContent] = useState<
    'loading' | 'pix' | 'pix_mp' | 'credit' | 'crypto' | 'expired'
  >('loading')
  const isExpired = useMemo(() => {
    if (!purchase) return undefined
    return purchase.expireAt.getTime() < Date.now()
  }, [purchase])
  useEffect(() => {
    if (
      isExpired === undefined ||
      purchase?.paymentStatus === 'PAID' ||
      purchase?.paymentStatus === 'PROCESSING'
    ) {
      setContent('loading')
    } else {
      if (isExpired) {
        setContent('expired')
      } else if (purchase && purchase.charge.paymentType === 'BOLETO_PIX') {
        setContent('pix')
      } else if (
        purchase &&
        ['PIX', 'PIX_BRLA'].includes(purchase.charge.paymentType)
      ) {
        setContent('pix_mp')
      } else if (purchase && purchase.charge.paymentType === 'CREDIT_CARD') {
        setContent('credit')
      } else if (purchase && purchase.charge.paymentType === 'CRYPTO') {
        setContent('crypto')
      }
    }
  }, [isExpired, purchase])

  const shouldShowTimer = content !== 'crypto' && content !== 'loading'

  const contentEl = useMemo(() => {
    switch (content) {
      case 'loading':
        return loading
      case 'pix':
      case 'pix_mp':
        return <Pix purchase={purchase!} />
      case 'credit':
        return <StripeContainer purchase={purchase!} />
      case 'crypto':
        return (
          <Crypto
            purchaseCode={purchase!.charge.code}
            uuid={uuid}
            onModalClose={onMyCollection}
          />
        )
      case 'expired':
        return expired
    }
  }, [content])

  const transitionEl = useMemo(() => {
    return (
      <Fragment key='other'>
        <Box className={classes.spacing}>
          {shouldShowTimer && (
            <Chip
              variant='outlined'
              label={
                isExpired ? (
                  i18n
                    .t('authenticated.pendingPayment.expiredPayment')
                    .toLowerCase()
                ) : !purchase ? (
                  <LoaderDotsIndicator />
                ) : (
                  <span>
                    {i18n.t('authenticated.pendingPayment.timeLeft')}{' '}
                    <Countdown
                      timeRemaining={purchase?.expireAt.getTime() - Date.now()}
                      onFinish={() => setContent('expired')}
                      model={CountdownTextModel.CLOCK}
                    />
                  </span>
                )
              }
            />
          )}
        </Box>
        {contentEl}
      </Fragment>
    )
  }, [contentEl])

  return (
    <>
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        flexGrow={1}>
        {transitionEl}
      </Box>
    </>
  )
}
const PaymentView: React.FC<PaymentViewProps> = (props) => {
  const { purchase, drop, onMyCollection } = props
  const classes = useStyles()

  return (
    <>
      <Container className={classes.container}>
        <main style={{ display: 'flex' }}>
          <div className={classes.paymentPane}>
            <Box className={classes.spacing}>
              <Typography variant='h2' component='h1'>
                {purchase &&
                  ['BOLETO_PIX', 'PIX', 'PIX_BRLA'].includes(
                    purchase.charge.paymentType
                  ) &&
                  i18n.t('authenticated.pendingPayment.payWithPix')}
                {purchase &&
                  purchase.charge.paymentType === 'CREDIT_CARD' &&
                  i18n.t('authenticated.pendingPayment.payWithCreditCard')}
                {purchase &&
                  purchase.charge.paymentType === 'CRYPTO' &&
                  i18n.t('authenticated.pendingPayment.payWithCryptoCoin')}
                &nbsp;
              </Typography>
            </Box>
            <PaymentViewContent {...props} />
            <Hidden mdUp>
              <Box mt={2} mb={2}>
                <DropInfoMobile drop={drop} />
              </Box>
            </Hidden>
          </div>
          <Hidden smDown>
            <OfferPaymentBanner
              asset={drop?.asset}
              paymentProvider={purchase?.provider}
            />
          </Hidden>
        </main>
        <Box alignSelf='flex-start'>
          <BackButton
            text={i18n.t('authenticated.pendingPayment.myCollection')}
            className={classes.spacing}
            onClick={onMyCollection}
          />
        </Box>
      </Container>
    </>
  )
}
export default PaymentView
